import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium"
import { LeadRow, LeadTableLookupFilterGroup, LookupMap, StringOptionMap } from "./leadTableTypes"
import DataGridRowMenu from '../../../DataGrid/DataGridRowMenu';
import { buildBooleanDisplayColumn, buildLookupDisplayColumn, formatCurrencyFromCents, formatDateTime, formatLookupValue, renderBooleanChip, renderDateTime, renderStatusChip } from "./leadTableCellHelpers"
import buildLookupFilterOperators from "../filters/buildLookupFilterOperators";


const LOOKUP_FILTER_OPERATOR_VALUES = ['isAnyOf', 'isNotAnyOf']

type BuildLeadTableColumnsParams = {
  timezone: string
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  degreeProgramsByClient: LeadTableLookupFilterGroup[]
  campaignsByClient: LeadTableLookupFilterGroup[]
  statuses: StringOptionMap
  dispositions: StringOptionMap
  permitLandingPages: boolean
  userHasMultipleClients: boolean
  userHasMultipleVendors: boolean
  canDeleteLead: (row: LeadRow) => boolean
  onLeadDetails: (row: LeadRow) => void
  onDeleteLead: (row: LeadRow) => void
  clientCampaignCustomAttributes: string[]
}

function insertColumnsAfterField(
  columns: GridColDef<LeadRow>[],
  field: string,
  columnsToInsert: GridColDef<LeadRow>[]
): GridColDef<LeadRow>[] {
  const insertIndex = columns.findIndex((column) => column.field === field)

  if (insertIndex === -1) {
    return [...columns, ...columnsToInsert]
  }

  return [
    ...columns.slice(0, insertIndex + 1),
    ...columnsToInsert,
    ...columns.slice(insertIndex + 1),
  ]
}

function buildBaseColumns(params: {
  timezone: string
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  degreeProgramsByClient: LeadTableLookupFilterGroup[]
  campaignsByClient: LeadTableLookupFilterGroup[]
  statuses: StringOptionMap
  dispositions: StringOptionMap
  permitLandingPages: boolean
  userHasMultipleClients: boolean
  userHasMultipleVendors: boolean
  canDeleteLead: (row: LeadRow) => boolean
  onLeadDetails: (row: LeadRow) => void
  onDeleteLead: (row: LeadRow) => void
}): GridColDef<LeadRow>[] {
  const {
    timezone,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    degreeProgramsByClient,
    campaignsByClient,
    statuses,
    dispositions,
    permitLandingPages,
    userHasMultipleClients,
    userHasMultipleVendors,
    canDeleteLead,
    onLeadDetails,
    onDeleteLead,
  } = params

  const renderDateCell = renderDateTime(timezone)

  const statusFilterOperators = buildLookupFilterOperators({
    lookup: statuses,
    searchable: true,
    showBulkToggles: true,
  }).filter((operator) => LOOKUP_FILTER_OPERATOR_VALUES.includes(operator.value))

  const dispositionFilterOperators = buildLookupFilterOperators({
    lookup: dispositions,
    searchable: true,
    showBulkToggles: true,
  }).filter((operator) => LOOKUP_FILTER_OPERATOR_VALUES.includes(operator.value))

  const columns: GridColDef<LeadRow>[] = [
    {
      field: 'createdAt',
      headerName: 'Date',
      minWidth: 170,
      filterable: false,
      valueFormatter: (value) => formatDateTime(value, timezone),
      renderCell: renderDateCell,
      flex: 1,
    },
    {
      field: 'publicId',
      headerName: 'VEGA ID',
      minWidth: 130,
      flex: 1,
    },
    buildLookupDisplayColumn({
      field: 'clientId',
      headerName: 'Client',
      lookup: clientIdToName,
      minWidth: 160,
      flex: 1,
    }),
    buildLookupDisplayColumn({
      field: 'degreeProgramId',
      headerName: 'Degree Program',
      lookup: degreeProgramIdToName,
      groups: degreeProgramsByClient,
      minWidth: 205,
      flex: 2.5,
    }),
    buildLookupDisplayColumn({
      field: 'vendorId',
      headerName: 'Vendor',
      lookup: vendorIdToName,
      minWidth: 160,
      flex: 1,
    }),
    buildLookupDisplayColumn({
      field: 'clientCampaignId',
      headerName: 'Campaign Name',
      lookup: clientCampaignIdToName,
      groups: campaignsByClient,
      minWidth: 160,
      filterable: true,
      flex: 2.5,
    }),
    {
      field: 'campaignPublicId',
      headerName: 'Campaign ID',
      minWidth: 140,
      flex: 2.5,
    },
    {
      field: 'subId',
      headerName: 'SubID',
      minWidth: 140,
      flex: 2.5,
    },
    {
      field: 'source',
      headerName: 'Source',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'firstName',
      headerName: 'First name',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 220,
      flex: 1.5,
    },
    buildLookupDisplayColumn({
      field: 'status',
      headerName: 'Status',
      lookup: statuses,
      minWidth: 155,
      filterOperators: statusFilterOperators,
      renderCell: (params) => renderStatusChip(formatLookupValue(params.row.status, statuses)),
    }),
    buildBooleanDisplayColumn({
      field: 'billable',
      headerName: 'Billable',
      minWidth: 160,
      renderCell: renderBooleanChip,
    }),
    {
      field: 'costInCents',
      headerName: 'Lead amount',
      minWidth: 130,
      type: 'number',
      valueFormatter: formatCurrencyFromCents,
      flex: 1,
    },
    {
      field: 'filteredReason',
      headerName: 'Filtered reason',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'externalId',
      headerName: 'External ID',
      minWidth: 140,
      flex: 2
    },
    buildBooleanDisplayColumn({
      field: 'test',
      headerName: 'Test',
      minWidth: 150,
      renderCell: renderBooleanChip,
    }),
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      minWidth: 160,
      flex: 1,
    },
    {
      field: 'streetAddress',
      headerName: 'Street Address',
      minWidth: 220,
      flex: 2.5
    },
    {
      field: 'city',
      headerName: 'City',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'state',
      headerName: 'State',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'zipCode',
      headerName: 'ZIP Code',
      minWidth: 110,
      flex: 1,
    },
    {
      field: 'statusCode',
      headerName: 'Status Code',
      minWidth: 120,
      flex: 1,
    },
    buildLookupDisplayColumn({
      field: 'disposition',
      headerName: 'Disposition',
      lookup: dispositions,
      minWidth: 180,
      filterOperators: dispositionFilterOperators,
    }),
    {
      field: 'statusChangedAt',
      headerName: 'Status changed',
      minWidth: 205,
      filterable: false,
      valueFormatter: (value) => formatDateTime(value, timezone),
      renderCell: renderDateCell,
      flex: 1,
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'userAgent',
      headerName: 'User Agent',
      minWidth: 280,
      flex: 3
    },
    buildBooleanDisplayColumn({
      field: 'secondaryTcpa',
      headerName: 'Secondary TCPA',
      minWidth: 210,
      renderCell: renderBooleanChip,
    }),
    {
      field: 'rejectionReason',
      headerName: 'Rejection reason',
      minWidth: 180,
      flex: 3
    },
    {
      field: 'rawRequestParameters',
      headerName: 'Raw request parameters',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'trackingParameters',
      headerName: 'Tracking parameters',
      minWidth: 220,
      flex: 3
    },
    {
      field: 'rowMenu',
      headerName: '',
      renderCell: (params: GridRenderCellParams<LeadRow>) => {
        return (
          <DataGridRowMenu
            row={params.row}
            label={`Lead ${params.row.id} menu`}
            menuWidth={140}
            items={[
              {
                key: 'lead-details',
                label: 'Lead Details',
                onClick: onLeadDetails,
              },
              ...(canDeleteLead(params.row)
                ? [
                    {
                      key: 'delete-lead',
                      label: 'Delete Lead',
                      onClick: onDeleteLead,
                    },
                  ]
                : []),
            ]}
          />
        )
      },
      hideable: false,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      width: 52,
      disableColumnMenu: true,
      pinnable: false,
      headerClassName: 'menu-col',
      cellClassName: 'menu-col',
    },
  ]

  if (permitLandingPages) {
    columns.push({
      field: 'landingPageId',
      headerName: 'Landing Page ID',
      minWidth: 150,
    })
  }

  return columns
    .filter((column) => userHasMultipleClients || column.field !== 'clientId')
    .filter((column) => userHasMultipleVendors || column.field !== 'vendorId')
}

function buildCustomAttributeColumns(
  clientCampaignCustomAttributes: string[]
): GridColDef<LeadRow>[] {
  return clientCampaignCustomAttributes.map((attribute) => ({
    field: `clientCampaignCustomAttributes:${attribute}`,
    headerName: attribute,
    minWidth: 180,
    sortable: true,
    filterable: true,
    valueGetter: (_value, row) => row.clientCampaignCustomAttributes?.[attribute] ?? '',
    renderCell: (params) => {
      const value = params.value == null ? '' : String(params.value)
      return <span title={value}>{value}</span>
    },
  }))
}

export function buildLeadTableColumns(
  params: BuildLeadTableColumnsParams
): GridColDef<LeadRow>[] {
  const baseColumns = buildBaseColumns(params)
  const customAttributeColumns = buildCustomAttributeColumns(
    params.clientCampaignCustomAttributes
  )

  return insertColumnsAfterField(baseColumns, 'billable', customAttributeColumns)
}
